// src/features/legacy-reports/components/LegacyMediaTab.tsx

import { useMemo, useState } from 'react';
import {
  Alert,
  Drawer,
  Group,
  Paper,
  Skeleton,
  Stack,
  Table,
  Text,
} from '@mantine/core';
import { IconAlertTriangle } from '@tabler/icons-react';
import { bcLong } from '@lib2/model/util/string/BaseConverter';
import { CsvDownloadButton } from '@/shared/components/CsvDownloadButton';
import { useVideos } from '@/features/media/useVideos';
import { useStreams } from '@/features/streams/useStreams';
import { LegacyReportsApi } from '../LegacyReportsApi';
import { LegacyReportsUtils } from '../LegacyReportsUtils';
import { useLegacyMediasReport } from '../useLegacyMediasReport';
import { useLegacyMediasDetailReport } from '../useLegacyMediasDetailReport';
import { SortableTh } from '@/shared/components/SortableTh';
import { useTableSort, type TableSortColumn } from '@/shared/hooks/useTableSort';
import { LegacyReportSections } from './LegacyReportSections';
import { LegacyTotalsCards } from './LegacyTotalsCards';
import type { LegacyMediaIdTotals } from '../types';

interface LegacyMediaTabProps {
  cId: number | null;
  start: number;
  end: number;
}

/** Subtypes rendered in the drill-down and exported in the detail CSV. */
const DETAIL_SUBTYPES = ['timeseries', 'geocountry', 'embedurl'] as const;

type MediaSortKey = 'title' | 'views' | 'viewers' | 'watch' | 'embeds';

export function LegacyMediaTab({ cId, start, end }: LegacyMediaTabProps) {
  const { report, isLoading, isError, error } = useLegacyMediasReport(cId, start, end);
  // Titles join client-side from the existing media library hooks, matching
  // the legacy dashboard behavior (media ids on this surface are numeric).
  const { videos } = useVideos();
  const { streams } = useStreams();
  const [expandedMediaId, setExpandedMediaId] = useState<number | null>(null);

  const titleByMediaId = useMemo(() => {
    const map = new Map<number, string>();
    for (const video of videos) map.set(video.media.mediaId, video.media.title);
    for (const stream of streams) map.set(stream.media.mediaId, stream.media.title);
    return map;
  }, [videos, streams]);

  const detail = useLegacyMediasDetailReport(
    cId,
    start,
    end,
    expandedMediaId !== null ? [expandedMediaId] : null
  );

  const rows = report?.mediaIdTotals ?? [];

  const titleOf = (mediaId: number): string =>
    titleByMediaId.get(mediaId) ?? bcLong.getStr(mediaId);

  const sortColumns = useMemo<Record<MediaSortKey, TableSortColumn<LegacyMediaIdTotals>>>(
    () => ({
      title: { accessor: (r) => titleByMediaId.get(r.mediaId) ?? bcLong.getStr(r.mediaId) },
      views: { accessor: (r) => r.totals.cPlays, defaultDir: 'desc' },
      viewers: { accessor: (r) => r.totals.cUniques, defaultDir: 'desc' },
      watch: { accessor: (r) => r.totals.cDuration, defaultDir: 'desc' },
      embeds: { accessor: (r) => r.embedUrlCount, defaultDir: 'desc' },
    }),
    [titleByMediaId],
  );
  const { sorted: sortedRows, sortKey, sortDir, onSort } = useTableSort(rows, sortColumns, {
    key: 'views',
    dir: 'desc',
  });

  if (isError) {
    return (
      <Alert color="yellow" icon={<IconAlertTriangle size={16} />} title="Report failed to load">
        {error?.message ?? 'Please try again.'}
      </Alert>
    );
  }

  return (
    <Stack gap="md">
      <Group justify="flex-end">
        {cId !== null && (
          <CsvDownloadButton
            download={() => LegacyReportsApi.downloadMediasCsv(cId, start, end)}
            fallbackFilename="medias_report.csv"
            label="Download media CSV"
          />
        )}
      </Group>

      {isLoading ? (
        <Skeleton height={240} />
      ) : rows.length === 0 ? (
        <Text c="dimmed">No media activity in this range.</Text>
      ) : (
        <Paper withBorder>
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <SortableTh columnKey="title" sortKey={sortKey} sortDir={sortDir} onSort={onSort}>
                  Title
                </SortableTh>
                <SortableTh columnKey="views" sortKey={sortKey} sortDir={sortDir} onSort={onSort} ta="right">
                  Views
                </SortableTh>
                <SortableTh columnKey="viewers" sortKey={sortKey} sortDir={sortDir} onSort={onSort} ta="right">
                  Viewers
                </SortableTh>
                <SortableTh columnKey="watch" sortKey={sortKey} sortDir={sortDir} onSort={onSort} ta="right">
                  Watch time
                </SortableTh>
                <SortableTh columnKey="embeds" sortKey={sortKey} sortDir={sortDir} onSort={onSort} ta="right">
                  Embeds
                </SortableTh>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {sortedRows.map((row) => (
                <Table.Tr
                  key={row.mediaId}
                  style={{ cursor: 'pointer' }}
                  onClick={() => setExpandedMediaId(row.mediaId)}
                >
                  <Table.Td>{titleOf(row.mediaId)}</Table.Td>
                  <Table.Td style={{ textAlign: 'right' }}>
                    {LegacyReportsUtils.formatCount(row.totals.cPlays)}
                  </Table.Td>
                  <Table.Td style={{ textAlign: 'right' }}>
                    {LegacyReportsUtils.formatCount(row.totals.cUniques)}
                  </Table.Td>
                  <Table.Td style={{ textAlign: 'right' }}>
                    {LegacyReportsUtils.formatWatchSeconds(row.totals.cDuration)}
                  </Table.Td>
                  <Table.Td style={{ textAlign: 'right' }}>
                    {LegacyReportsUtils.formatCount(row.embedUrlCount)}
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Paper>
      )}

      <Drawer
        opened={expandedMediaId !== null}
        onClose={() => setExpandedMediaId(null)}
        title={expandedMediaId !== null ? titleOf(expandedMediaId) : ''}
        position="right"
        size="xl"
      >
        {expandedMediaId !== null && (
          <Stack gap="md">
            <Group justify="flex-end">
              {cId !== null && (
                <CsvDownloadButton
                  download={() =>
                    LegacyReportsApi.downloadMediasDetailCsv(
                      cId,
                      start,
                      end,
                      [expandedMediaId],
                      [...DETAIL_SUBTYPES]
                    )
                  }
                  fallbackFilename="medias_detail_report.csv"
                  label="Download detail CSV"
                />
              )}
            </Group>
            {detail.isError ? (
              <Alert
                color="yellow"
                icon={<IconAlertTriangle size={16} />}
                title="Detail failed to load"
              >
                {detail.error?.message ?? 'Please try again.'}
              </Alert>
            ) : (
              <>
                <LegacyTotalsCards
                  totals={detail.report?.totals ?? null}
                  isLoading={detail.isLoading}
                />
                <LegacyReportSections
                  timeseriesReport={detail.report?.timeseriesReport}
                  geoCountryReport={detail.report?.geoCountryReport}
                  embedUrlReport={detail.report?.embedUrlReport}
                  isLoading={detail.isLoading}
                />
              </>
            )}
          </Stack>
        )}
      </Drawer>
    </Stack>
  );
}
